import React, { createContext, useState, useCallback, useEffect } from 'react';
import axios from '../axiosInstance.js';

const AuthContext = createContext();

function deepMerge(target, patch) {
    if (patch === null || patch === undefined) return target;

    if (typeof patch !== 'object' || Array.isArray(patch)) return patch;

    const base = (target && typeof target === 'object' && !Array.isArray(target)) ? target : {};
    const out = { ...base };

    for (const key of Object.keys(patch)) {
        const p = patch[key];
        const b = base[key];

        if (
            p !== null && typeof p === 'object' && !Array.isArray(p) &&
            b !== null && typeof b === 'object' && !Array.isArray(b)
        ) {
            out[key] = deepMerge(b, p);
        } else {
            out[key] = p;
        }
    }

    return out;
}

const AuthProvider = ({ children }) => {

    const [user, setUser] = useState(null);
    const [authReady, setAuthReady] = useState(false);

    const login = useCallback((userData) => {
        setUser(userData);
    }, []);

    const logout = useCallback(() => {
        setUser(null);
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');

        if (!token) {
            setAuthReady(true);
            return;
        }

        let cancelled = false;

        axios.get('/api/account/info', {
            headers: { Authorization: `Bearer ${token}` },
        }).then((res) => {
            if (!cancelled && res.data) {
                setUser(res.data);
            }
        }).catch((err) => {
            console.log(err);
            try {
                localStorage.removeItem('token');
            } catch (_) { /* ignore storage errors */ }
            if (!cancelled) {
                setUser(null);
            }
        }).finally(() => {
            if (!cancelled) {
                setAuthReady(true);
            }
        });

        return () => {
            cancelled = true;
        };
    }, []);

    // Persists a partial settings patch to the backend and merges the result
    // into the local user object optimistically for instant UI feedback.
    const updateSettings = useCallback(async (patch) => {
        setUser((prev) => prev ? { ...prev, settings: deepMerge(prev.settings, patch) } : prev);

        try {
            const res = await axios.post('/api/account/settings', patch, {
                headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
            });

            if (res?.data?.settings) {
                setUser((prev) => prev ? { ...prev, settings: res.data.settings } : prev);
            }

            return res?.data?.settings;
        } catch (err) {
            console.log('Failed to persist settings', err);
        }
    }, []);

    return (
        <AuthContext.Provider value={{
            user,
            authReady,
            login,
            logout,
            updateSettings,
        }}
        >
            {children}
        </AuthContext.Provider>
    );

};

export { AuthProvider, AuthContext };
